import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import omit from 'lodash/omit';

import { ItemsFilters } from '../../../../types';
import { TeamNanoID } from '../../../teams/teamsTypes';

import { isEveryPropEmpty } from '../../../../utils/isEveryPropEmpty';

import { StockTabs } from '../../stockConstants';
import { StockPath } from '../../StockPath';
import { TeamPath } from '../../../teams/TeamPath';

type ChangePathQueryFiltersOptions = {
  currentTab: StockTabs;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  onIndexVisit?: () => void;
};

function useChangePathQueryFilters({
  currentTab,
  companyNanoId,
  isMyLibrary,
  onIndexVisit
}: ChangePathQueryFiltersOptions) {
  const { push, replace, pathname: defaultPathname, query } = useRouter();

  useEffect(() => {
    if (query?.indexPage) {
      onIndexVisit?.();
      replace({
        pathname: defaultPathname,
        query: omit(query, 'indexPage')
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const changePathQueryFilters = useCallback<
    (tab: StockTabs, filters?: ItemsFilters) => void
  >(
    (tab, filters) => {
      const indexPage = currentTab !== tab;

      const query = isEveryPropEmpty(filters)
        ? null
        : { filters: JSON.stringify(filters), indexPage };

      let pathname = defaultPathname;

      switch (tab) {
        case StockTabs.PRODUCTS:
          pathname = isMyLibrary
            ? TeamPath.currentCompanyNewLibraryProductsPure()
            : companyNanoId
            ? TeamPath.companyNewLibraryProductsPure(companyNanoId)
            : StockPath.newProductsPure();
          break;

        case StockTabs.LIFESTYLES:
          pathname = isMyLibrary
            ? TeamPath.currentCompanyNewLibraryLifestylesPure()
            : companyNanoId
            ? TeamPath.companyNewLibraryLifestylesPure(companyNanoId)
            : StockPath.newLifestylesPure();
          break;

        case StockTabs.MATERIALS:
          pathname = isMyLibrary
            ? TeamPath.currentCompanyNewLibraryMaterialsPure()
            : companyNanoId
            ? TeamPath.companyNewLibraryMaterialsPure(companyNanoId)
            : StockPath.newMaterialsPure();
          break;
      }

      push({ pathname, query });
    },
    [companyNanoId, currentTab, defaultPathname, isMyLibrary, push]
  );

  return {
    changePathQueryFilters
  };
}

export default useChangePathQueryFilters;
